import {
  type Widget,
  type WidgetData,
  type WidgetSizeDefinition,
  type WidgetType,
  type WorkspaceTemplateConfig,
  type WorkspaceTemplateWidget,
  type WorkspaceTemplateWidgetEntry,
  deleteWidgetPreferences,
  updateWidgetPreferences,
  useWidgetPreferences,
} from "@repo/api";
import { t } from "@repo/translation";
import {
  STORAGE_KEY,
  getStorageItem,
  setStorageItem,
} from "@repo/ui/src/storage/storage";
import { type Toast } from "@repo/utils";
import { useCallback, useState } from "react";
import { mutate } from "swr";

const GRID_WIDTH = 4;

const DEFAULT_SIZE: WidgetSizeDefinition = { size: "S", col: 1, row: 1 };

function normalizeTemplateEntry(
  entry: WorkspaceTemplateWidgetEntry
): WorkspaceTemplateWidget {
  return typeof entry === "string" ? { widgetType: entry } : entry;
}

function getSizeForWidget(
  widget: Widget,
  requestedSize: WorkspaceTemplateWidget["appSize"]
): WidgetSizeDefinition {
  const sizes = widget.sizeDefinition;
  if (!sizes?.length) {
    return DEFAULT_SIZE;
  }
  if (requestedSize) {
    const match = sizes.find((s) => s.size === requestedSize);
    if (match) {
      return match;
    }
  }
  return sizes.find((s) => s.size === "M") ?? sizes[sizes.length - 1];
}

function calculatePositions(
  entries: WorkspaceTemplateWidget[],
  availableWidgets: Widget[]
) {
  const positions: {
    widgetType: WidgetType;
    size: WidgetSizeDefinition;
    top: number;
    left: number;
  }[] = [];
  let currentRow = 0;
  let currentLeft = 0;
  let maxRowHeight = 1;

  for (const entry of entries) {
    const widget = availableWidgets.find(
      (w) => w.widgetType === entry.widgetType
    );
    const size = widget
      ? getSizeForWidget(widget, entry.appSize)
      : DEFAULT_SIZE;

    if (currentLeft + size.col > GRID_WIDTH) {
      currentRow += maxRowHeight;
      currentLeft = 0;
      maxRowHeight = 1;
    }

    positions.push({
      widgetType: entry.widgetType,
      size,
      top: currentRow,
      left: currentLeft,
    });
    currentLeft += size.col;
    maxRowHeight = Math.max(maxRowHeight, size.row);
  }

  return positions;
}

export const useApplyTemplate = (
  updateToast: (toast: Toast) => void,
  availableWidgets: Widget[] = []
) => {
  const { data: existingWidgets } = useWidgetPreferences();
  const [isApplying, setIsApplying] = useState(false);

  const applyTemplate = useCallback(
    async (template: WorkspaceTemplateConfig) => {
      setIsApplying(true);

      try {
        if (existingWidgets?.length) {
          await Promise.all(
            existingWidgets.map((widget) =>
              deleteWidgetPreferences(widget.workspacePreferenceId)
            )
          );
        }

        const normalizedEntries = template.widgetTypes.map(
          normalizeTemplateEntry
        );

        const widgetPositions = calculatePositions(
          normalizedEntries,
          availableWidgets
        );

        for (const item of widgetPositions) {
          const widget = availableWidgets.find(
            (w) => w.widgetType === item.widgetType
          );

          if (!widget) {
            continue;
          }

          const widgetData: Widget & Omit<WidgetData, "workspacePreferenceId"> =
            {
              ...widget,
              widgetSettings: {
                appSize: item.size,
                appSetting: {},
                top: item.top,
                left: item.left,
              },
            };

          await updateWidgetPreferences(widgetData, true);
        }

        await mutate("/coworkers/widgetpreferences");

        setStorageItem(STORAGE_KEY.LAST_APPLIED_TEMPLATE, template.name);

        updateToast({
          id: template.name,
          title: t("prompt.template_applied"),
        });
      } catch {
        updateToast({
          title: t("prompt.template_apply_failed"),
          type: "error",
        });
      } finally {
        setIsApplying(false);
      }
    },
    [existingWidgets, updateToast, availableWidgets]
  );

  const lastAppliedTemplate =
    getStorageItem(STORAGE_KEY.LAST_APPLIED_TEMPLATE) || "";

  return {
    applyTemplate,
    isApplying,
    lastAppliedTemplate,
  };
};
